/**
 * Normaliza o usuário do /me / localStorage (com ou sem wrapper `data`).
 * @param {object|null} raw
 * @returns {object|null}
 */
export function resolveAuthUser(raw = null) {
  const source = raw ?? (() => {
    try {
      return JSON.parse(localStorage.getItem('userData') || 'null')
    } catch (e) {
      return null
    }
  })()
  if (!source) return null
  return source.data || source
}

/**
 * @param {object|null} user
 * @returns {object|null}
 */
export function getStoreFromUser(user = null) {
  const resolved = user ? resolveAuthUser(user) : resolveAuthUser()
  return resolved?.store || null
}

/**
 * Loja sob restrição de capabilities (jornada eSemente):
 * - settings.origin === 'esemente360', ou
 * - settings.onboarding definido (mesmo sem origin — lojas provisionadas incompletas)
 * @param {object|null} store
 * @returns {boolean}
 */
export function isEsementeStore(store = null) {
  if (!store) return false
  const settings = store.settings || {}
  if (settings.origin === 'esemente360') return true
  return Object.prototype.hasOwnProperty.call(settings, 'onboarding')
}

/**
 * @param {object|null} store
 * @returns {string[]|null} null = sem restrição (loja legada)
 */
export function getStoreCapabilities(store = null) {
  if (!store) return null

  // Legado: plan pode ser "ecommerce" por default da migration — não restringe
  if (!isEsementeStore(store)) {
    return null
  }

  if (Object.prototype.hasOwnProperty.call(store, 'capabilities')) {
    return store.capabilities
  }

  if (store.plan) {
    return []
  }

  return null
}

/**
 * @param {object|null} store
 * @param {string|string[]|undefined|null} required
 * @returns {boolean}
 *
 * Nota: `onboarding` é exclusivo eSemente — loja legada (capabilities null) não passa.
 */
export function storeHasCapability(store, required) {
  if (!required) return true

  const requiredList = Array.isArray(required) ? required : [required]

  // Onboarding é exclusivo da jornada eSemente (legado não deve ver menu/rota)
  if (requiredList.includes('onboarding') && !isEsementeStore(store)) {
    return false
  }

  const capabilities = getStoreCapabilities(store)
  // null = loja sem restrição (legado)
  if (capabilities === null || capabilities === undefined) return true

  return requiredList.every(code => Array.isArray(capabilities) && capabilities.includes(code))
}

/**
 * @param {object|null} user
 * @param {string|string[]|undefined|null} required
 * @returns {boolean}
 */
export function userHasCapability(user, required) {
  return storeHasCapability(getStoreFromUser(user), required)
}

/**
 * Rota inicial do painel:
 * - eSemente + onboarding pendente → onboarding
 * - eSemente + onboarding ok → products.index
 * - legado → home
 * @param {object|null} userOrStore
 * @returns {string}
 */
export function getDefaultRouteName(userOrStore = null) {
  let store = null
  if (userOrStore && (Object.prototype.hasOwnProperty.call(userOrStore, 'plan')
    || Object.prototype.hasOwnProperty.call(userOrStore, 'capabilities')
    || userOrStore.settings)
    && !userOrStore.store) {
    store = userOrStore
  } else {
    store = getStoreFromUser(userOrStore)
  }

  if (!isEsementeStore(store)) {
    return 'home'
  }

  const onboardingDone = store?.settings?.onboarding === true
  return onboardingDone ? 'products.index' : 'onboarding'
}
